// src/features/tidsregistering/pages/SickLeaveTrackerPage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  NoteAdd as NoteAddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useSickLeaveTrackers } from '../hooks/useSickLeaveTrackers';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useTidsregistreringSickLeavePermissions } from '../../auth/hooks/usePermissions';
import StaffMember from '../../../components/common/StaffMember';

import PageHeader from '../../../components/common/PageHeader';
import SickIcon from '@mui/icons-material/Sick';

function DoctorNoteDialog({ open, staffMember, tracker, onClose, onSave }) {
  const [noteDate, setNoteDate] = useState(new Date());
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(staffMember.staff_member_id, format(noteDate, 'yyyy-MM-dd'));
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Registrer lægeattest</DialogTitle>
      <DialogContent>
        {staffMember && (
          <Box sx={{ mb: 2 }}>
            <StaffMember staffMember={staffMember} avatarSize={28} gap={1} nameVariant="body2" clickable={false} />
          </Box>
        )}
        <>
          <DatePicker
            label="Dato modtaget"
            value={noteDate}
            onChange={(d) => d && setNoteDate(d)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </>
        {tracker?.last_doctor_note && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Sidst registreret: {tracker.last_doctor_note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SickLeaveTrackerPage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { trackers, fetchForDepartment, rebuildTracker, recordDoctorNote, loading, error } = useSickLeaveTrackers();
  const { staff, fetchDepartmentStaff } = useStaff();
  // Registering a doctor's note and recalculating are administration
  const { canAdmin } = useTidsregistreringSickLeavePermissions();
  const [search, setSearch] = useState('');
  const [noteDialog, setNoteDialog] = useState(null); // { staffMember, tracker }

  const staffMap = Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s]));
  const trackerMap = Object.fromEntries(trackers.map((t) => [t.staff_member_id, t]));

  useEffect(() => {
    fetchDepartmentStaff();
    fetchForDepartment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRebuild = useCallback(async (staffMemberId) => {
    const result = await rebuildTracker({ staff_member_id: staffMemberId });
    if (result.success) showSuccess('Tracker genberegnet');
    else showError(result.error ?? 'Kunne ikke genberegne');
  }, [rebuildTracker, showSuccess, showError]);

  const handleRecordNote = useCallback(async (staffMemberId, noteDate) => {
    const result = await recordDoctorNote({ staff_member_id: staffMemberId, note_received_date: noteDate });
    if (result.success) {
      showSuccess('Lægeattest registreret');
      setNoteDialog(null);
    } else {
      showError(result.error ?? 'Kunne ikke registrere');
    }
  }, [recordDoctorNote, showSuccess, showError]);

  const displayedStaff = (staff ?? []).filter((s) =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase())
  );

  const longTermCount = trackers.filter((t) => t.long_term_threshold_reached).length;
  const activeCount = trackers.filter((t) => t.consecutive_days > 0).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={SickIcon}
        title={t('common:navigation.tidsregistering_sick_leave')}
        subtitle={t('tidsregistering:page_subtitles.sick_leave')}
        badges={<>
          {activeCount > 0 && (
            <Chip label={`${activeCount} aktive`} color="warning" size="small" />
          )}
          {longTermCount > 0 && (
            <Chip label={`${longTermCount} langvarigt`} color="error" size="small" icon={<WarningIcon />} />
          )}
        </>}
      />

      {/* Search */}
      <Box>
        <TextField
          size="small"
          placeholder="Søg medarbejder…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 240 }}
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Medarbejder</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Konsek. dage</TableCell>
              <TableCell>Sygeperiode start</TableCell>
              <TableCell>Seneste lægeattest</TableCell>
              {canAdmin && <TableCell align="right">Handlinger</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && trackers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canAdmin ? 6 : 5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : displayedStaff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canAdmin ? 6 : 5} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Ingen medarbejdere fundet
                </TableCell>
              </TableRow>
            ) : displayedStaff.map((member) => {
              const tracker = trackerMap[member.staff_member_id];
              const isActive = tracker && tracker.consecutive_days > 0;
              const isLongTerm = tracker?.long_term_threshold_reached;

              return (
                <TableRow
                  key={member.staff_member_id}
                  hover
                  sx={{ bgcolor: isLongTerm ? 'error.50' : isActive ? 'warning.50' : undefined }}
                >
                  <TableCell>
                    <StaffMember staffMember={member} avatarSize={28} gap={1} nameVariant="body2" clickable={false} />
                  </TableCell>
                  <TableCell align="center">
                    {isLongTerm ? (
                      <Chip label="Langvarigt" color="error" size="small" variant="outlined" icon={<WarningIcon />} />
                    ) : isActive ? (
                      <Chip label="Sygemeldt" color="warning" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Rask" color="success" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={isActive ? 600 : undefined}
                      color={isLongTerm ? 'error.main' : isActive ? 'warning.main' : 'text.secondary'}
                    >
                      {tracker ? tracker.consecutive_days : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {tracker?.current_spell_start ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={!tracker?.last_doctor_note && isLongTerm ? 'error.main' : 'text.secondary'}>
                      {tracker?.last_doctor_note ?? (isLongTerm ? 'Mangler!' : '—')}
                    </Typography>
                  </TableCell>
                  {canAdmin && <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Registrer lægeattest">
                        <IconButton
                          size="small"
                          disabled={!tracker}
                          onClick={() => setNoteDialog({ staffMember: member, tracker })}
                        >
                          <NoteAddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Genberegn tracker">
                        <IconButton
                          size="small"
                          onClick={() => handleRebuild(member.staff_member_id)}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DoctorNoteDialog
        open={!!noteDialog}
        staffMember={noteDialog?.staffMember ?? null}
        tracker={noteDialog?.tracker ?? null}
        onClose={() => setNoteDialog(null)}
        onSave={handleRecordNote}
      />
    </Box>
  );
}
